import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { TextInput, DatePicker } from '@/components/ui';
import Select from '@workpay/ds-shared/components/ui/Select';
import MultiSelect from '@workpay/ds-shared/components/ui/MultiSelect';
import Stepper from '@/components/ui/Stepper/Stepper';
import type { StepConfig } from '@/components/ui/Stepper/types';
import StepperModalFooter from '@/components/shared/StepperModalFooter/StepperModalFooter';
import MandatoryFormRenderer from '@components/shared/MandatoryFormRenderer/MandatoryFormRenderer';
import useMultiStepForm from '@/hooks/useMultiStepForm';
import { useTranslation } from '@/hooks/useTranslation';
import { useCountries } from '@/hooks/useCountries';
import { getEmploymentTypes } from '@/services/employmentTypeService';
import { getFormTemplateByScope, filterFormTemplate } from '@/services/formTemplateService';
import { useAuthStore } from '@/stores/authStore';
import { useNotificationStore } from '@stores/notificationStore';
import { WorkpayApiError } from '@lib/apiEnvelope';
import { useReferenceDataStore } from '@/stores/referenceDataStore';
import { createLegalEntity } from '@/services/legalEntityService';
import type { FormTemplateTab } from '@/types/formTemplate';
import type { CreateLegalEntityPayload } from '@/types/legalEntity';
import { toApiDateTime } from '@utils/dateHelpers';
import styles from './CreateEntityModal.module.scss';

// ============================================
// TYPES
// ============================================

interface CreateEntityModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface EntityFormValues {
  name: string;
  country: string;
  entityType: string;
  employmentTypeIds: string[];
  effectiveFrom: string;
}

const STEP_IDS = ['details', 'forms'] as const;

const STEP_FIELDS: Record<number, (keyof EntityFormValues)[]> = {
  0: ['name', 'country', 'entityType', 'effectiveFrom'],
  1: [],
};

// Entity type option values (labels translated inside component)
const ENTITY_TYPE_VALUES = ['WORKPAY_OWNED', 'PARTNER'] as const;

const DEFAULT_FORM_VALUES: EntityFormValues = {
  name: '',
  country: '',
  entityType: '',
  employmentTypeIds: [],
  effectiveFrom: '',
};

// ============================================
// COMPONENT
// ============================================

function CreateEntityModal({ isOpen, onClose }: CreateEntityModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const session = useAuthStore((s) => s.session);
  const orgType = session?.organization.type ?? '';
  const orgId = session?.organization.id ?? '';

  const STEPS: StepConfig[] = useMemo(() => [
    { id: STEP_IDS[0], label: t('K_STEP_ENTITY_DETAILS', 'Entity Details') },
    { id: STEP_IDS[1], label: t('K_STEP_MANDATORY_FORMS', 'Mandatory Forms') },
  ], [t]);

  const ENTITY_TYPE_OPTIONS = useMemo(() => [
    { label: t('K_OPTION_WORKPAY_OWNED', 'Workpay Owned'), value: ENTITY_TYPE_VALUES[0] },
    { label: t('K_OPTION_PARTNER', 'Partner'), value: ENTITY_TYPE_VALUES[1] },
  ], [t]);

  // Resolved tabs from form template
  const [resolvedTabs, setResolvedTabs] = useState<FormTemplateTab[]>([]);
  const [resolveLoading, setResolveLoading] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);

  // Form data collected from mandatory Formio forms
  const [formDataMap, setFormDataMap] = useState<Record<string, Record<string, unknown>>>({});
  // Tracks whether each form has required Formio fields (for smart button blocking)
  const [requiredFieldsMap, setRequiredFieldsMap] = useState<Record<string, boolean>>({});

  const { countries, isLoading: countriesLoading } = useCountries();
  const countriesError = useReferenceDataStore((s) => s.countriesError);

  const countryOptions = useMemo(
    () => countries.map((c) => ({ label: c.name, value: c.id })),
    [countries],
  );

  const {
    data: employmentTypesData,
    isLoading: employmentTypesLoading,
    isError: employmentTypesIsError,
  } = useQuery({
    queryKey: ['employmentTypes', { size: 100 }],
    queryFn: () => getEmploymentTypes({ size: 100 }),
  });

  const employmentTypeOptions = useMemo(
    () => (employmentTypesData?.content ?? []).map((et) => ({ label: et.name, value: et.id })),
    [employmentTypesData],
  );

  const {
    register,
    handleSubmit,
    reset,
    control,
    trigger,
    getValues,
    formState: { errors },
  } = useForm<EntityFormValues>({
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'onChange',
  });

  // Fetch and resolve form template using Step 1 country
  const fetchResolvedTabs = useCallback(async (): Promise<boolean> => {
    const { country } = getValues();
    setResolveLoading(true);
    setResolveError(null);
    // formDataMap and requiredFieldsMap intentionally NOT reset —
    // persisted data is restored for forms that survive the filter change

    try {
      let template;
      try {
        template = await getFormTemplateByScope(orgType, orgId, 'LEGAL_ENTITY', {
          countryId: country || undefined,
        });
      } catch {
        // 404 — no template exists for this country
        setResolvedTabs([]);
        setResolveLoading(false);
        return true;
      }

      const resolved = await filterFormTemplate(template.id, {});

      setResolvedTabs(resolved.tabs);
      setResolveLoading(false);
      return true;
    } catch {
      setResolveError(t('K_FAILED_LOAD_FORM_TEMPLATES', 'Failed to load form templates. Please try again.'));
      setResolveLoading(false);
      addNotification({
        variant: 'critical',
        title: t('K_NOTIF_FAILED_LOAD_FORM_TEMPLATES', 'Failed to load form templates'),
        description: t('K_PLEASE_TRY_AGAIN', 'Please try again.'),
      });
      return false;
    }
  }, [getValues, orgType, orgId, t, addNotification]);

  const stepper = useMultiStepForm({
    totalSteps: STEPS.length,
    onValidateStep: async (stepIndex) => {
      const fields = STEP_FIELDS[stepIndex];
      if (fields && fields.length > 0) {
        const isValid = await trigger(fields);
        if (!isValid) return false;
      }

      // On Step 1 -> Step 2 transition, fetch resolved tabs
      if (stepIndex === 0) {
        return fetchResolvedTabs();
      }

      return true;
    },
  });

  // Client-side: filter mandatory forms from resolvedTabs
  const mandatoryForms = useMemo(() => {
    return resolvedTabs.flatMap((tab) =>
      tab.forms.filter((f) => f.attributes.isMandatory === true),
    );
  }, [resolvedTabs]);

  const mandatoryFormIds = useMemo(() => {
    return mandatoryForms.map((f) => f.formConfigId);
  }, [mandatoryForms]);

  const allMandatoryFormsFilled = useMemo(() => {
    if (mandatoryFormIds.length === 0) return true;
    return mandatoryFormIds.every(
      (id) => formDataMap[id] !== undefined || requiredFieldsMap[id] === false,
    );
  }, [mandatoryFormIds, formDataMap, requiredFieldsMap]);

  // Reset on modal open/close
  useEffect(() => {
    if (!isOpen) return;
    stepper.reset();
    setResolvedTabs([]);
    setResolveError(null);
    setFormDataMap({});
    setRequiredFieldsMap({});
    reset(DEFAULT_FORM_VALUES);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Surface countries load failure as a toast while the modal is open
  useEffect(() => {
    if (!isOpen || !countriesError) return;
    addNotification({
      variant: 'critical',
      title: t('K_NOTIF_FAILED_LOAD_COUNTRIES', 'Failed to load countries'),
      description: t('K_PLEASE_TRY_AGAIN', 'Please try again.'),
    });
  }, [isOpen, countriesError, addNotification, t]);

  // Surface employment-types load failure as a toast while the modal is open
  useEffect(() => {
    if (!isOpen || !employmentTypesIsError) return;
    addNotification({
      variant: 'critical',
      title: t('K_NOTIF_FAILED_LOAD_EMPLOYMENT_TYPES', 'Failed to load employment types'),
      description: t('K_PLEASE_TRY_AGAIN', 'Please try again.'),
    });
  }, [isOpen, employmentTypesIsError, addNotification, t]);

  const createMutation = useMutation({
    mutationFn: createLegalEntity,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['legalEntities'] });
      reset();
      stepper.reset();
      setResolvedTabs([]);
      setFormDataMap({});
      onClose();
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const handleFormSave = useCallback(
    (formConfigId: string, data: Record<string, unknown>) => {
      setFormDataMap((prev) => ({ ...prev, [formConfigId]: data }));
    },
    [],
  );

  const handleRequiredCheck = useCallback(
    (formConfigId: string, hasRequired: boolean) => {
      setRequiredFieldsMap((prev) => ({ ...prev, [formConfigId]: hasRequired }));
    },
    [],
  );

  /** Returns formDataMap entries that exist in the current resolvedTabs */
  const getRelevantFormData = useCallback((): Record<string, Record<string, unknown>> | undefined => {
    const currentFormIds = new Set(
      resolvedTabs.flatMap((tab) => tab.forms.map((f) => f.formConfigId)),
    );
    const filtered: Record<string, Record<string, unknown>> = {};
    for (const [id, data] of Object.entries(formDataMap)) {
      if (currentFormIds.has(id)) {
        filtered[id] = data;
      }
    }
    return Object.keys(filtered).length > 0 ? filtered : undefined;
  }, [resolvedTabs, formDataMap]);

  const onSubmit = useCallback(
    (data: EntityFormValues) => {
      const payload: CreateLegalEntityPayload = {
        name: data.name,
        countryId: data.country,
        entityType: (data.entityType as CreateLegalEntityPayload['entityType']) || undefined,
        employmentTypeIds: data.employmentTypeIds.length > 0 ? data.employmentTypeIds : undefined,
        effectiveFrom: toApiDateTime(data.effectiveFrom),
        tabs: resolvedTabs.length > 0 ? resolvedTabs : undefined,
        formData: getRelevantFormData(),
      };
      createMutation.mutate(payload);
    },
    [createMutation, resolvedTabs, getRelevantFormData],
  );

  const handleClose = useCallback(() => {
    if (!createMutation.isPending) {
      reset();
      stepper.reset();
      setResolvedTabs([]);
      setResolveError(null);
      setFormDataMap({});
      setRequiredFieldsMap({});
      onClose();
    }
  }, [createMutation.isPending, reset, stepper, onClose]);

  const handleNext = useCallback(async () => {
    if (stepper.isLastStep) {
      handleSubmit(onSubmit)();
    } else {
      await stepper.goToNext();
    }
  }, [stepper, handleSubmit, onSubmit]);

  const footer = useMemo(
    () => (
      <StepperModalFooter
        isFirstStep={stepper.isFirstStep}
        isLastStep={stepper.isLastStep}
        onNext={handleNext}
        onPrev={stepper.goToPrev}
        onCancel={handleClose}
        isSubmitting={createMutation.isPending || resolveLoading}
        submitLabel={t('K_SAVE', 'Save')}
        disableNext={stepper.isLastStep && !allMandatoryFormsFilled}
      />
    ),
    [stepper, handleNext, handleClose, createMutation.isPending, resolveLoading, allMandatoryFormsFilled],
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('K_MODAL_CREATE_ENTITY', 'Create Entity')}
      size="lg"
      footer={footer}
    >
      <Stepper
        steps={STEPS}
        activeStep={stepper.currentStep}
        onStepClick={stepper.goToStep}
      />

      <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.stepContainer}>
          {/* Step 1: Entity Details */}
          <div
            className={`${styles.step} ${
              stepper.currentStep === 0 ? styles.stepActive : styles.stepHidden
            } ${stepper.direction === 1 ? styles.slideLeft : styles.slideRight}`}
          >
            <div className={styles.row}>
              <TextInput
                label={t('K_LABEL_ENTITY_NAME', 'Entity Name')}
                isRequired
                placeholder={t('K_PLACEHOLDER_ENTITY_NAME', 'Enter entity name')}
                error={errors.name?.message}
                {...register('name', {
                  required: t('K_VALIDATION_ENTITY_NAME_REQUIRED', 'Entity name is required'),
                  maxLength: { value: 255, message: t('K_VALIDATION_MAX_255', 'Max 255 characters') },
                })}
              />
              <Controller
                name="country"
                control={control}
                rules={{ required: t('K_VALIDATION_COUNTRY_REQUIRED', 'Country is required') }}
                render={({ field }) => (
                  <Select
                    label={t('K_LABEL_COUNTRY', 'Country')}
                    isRequired
                    placeholder={t('K_PLACEHOLDER_COUNTRY', 'Select country')}
                    options={countryOptions}
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.country?.message}
                    loading={countriesLoading}
                    searchable
                  />
                )}
              />
            </div>

            <div className={styles.row}>
              <Controller
                name="entityType"
                control={control}
                rules={{ required: t('K_VALIDATION_ENTITY_TYPE_REQUIRED', 'Entity type is required') }}
                render={({ field }) => (
                  <Select
                    label={t('K_LABEL_ENTITY_TYPE', 'Entity Type')}
                    isRequired
                    placeholder={t('K_PLACEHOLDER_ENTITY_TYPE', 'Select entity type')}
                    options={ENTITY_TYPE_OPTIONS}
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.entityType?.message}
                  />
                )}
              />
              <Controller
                name="employmentTypeIds"
                control={control}
                render={({ field }) => (
                  <MultiSelect
                    label={t('K_LABEL_EMPLOYMENT_TYPES', 'Employment Types')}
                    placeholder={t('K_PLACEHOLDER_EMPLOYMENT_TYPES', 'Select employment types')}
                    options={employmentTypeOptions}
                    value={field.value}
                    onChange={field.onChange}
                    loading={employmentTypesLoading}
                  />
                )}
              />
            </div>

            <div className={styles.row}>
              <Controller
                name="effectiveFrom"
                control={control}
                rules={{ required: t('K_VALIDATION_EFFECTIVE_FROM_REQUIRED', 'Effective from date is required') }}
                render={({ field }) => (
                  <DatePicker
                    label={t('K_LABEL_EFFECTIVE_FROM', 'Effective From')}
                    isRequired
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.effectiveFrom?.message}
                    locale="en-GB"
                    fullWidth
                  />
                )}
              />
            </div>
          </div>

          {/* Step 2: Mandatory Forms */}
          <div
            className={`${styles.step} ${
              stepper.currentStep === 1 ? styles.stepActive : styles.stepHidden
            } ${stepper.direction === 1 ? styles.slideLeft : styles.slideRight}`}
          >
            {resolveLoading && (
              <p className={styles.placeholderText}>{t('K_LOADING_FORM_TEMPLATES', 'Loading form templates...')}</p>
            )}

            {resolveError && (
              <p className={styles.errorText}>{resolveError}</p>
            )}

            {!resolveLoading && !resolveError && mandatoryForms.length === 0 && (
              <p className={styles.placeholderText}>
                {t('K_NO_MANDATORY_FORMS', 'No mandatory forms to fill.')}
              </p>
            )}

            {!resolveLoading && !resolveError && mandatoryForms.length > 0 && (
              <div className={styles.tabAccordions}>
                {mandatoryForms.map((form) => (
                  <MandatoryFormRenderer
                    key={form.formConfigId}
                    formConfigId={form.formConfigId}
                    formName={form.formName ?? form.formConfigId}
                    initialSaved={formDataMap[form.formConfigId] !== undefined}
                    initialData={formDataMap[form.formConfigId]}
                    onSave={handleFormSave}
                    onRequiredCheck={handleRequiredCheck}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

      </form>
    </Modal>
  );
}

export default CreateEntityModal;
